/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import Config from "data/config";
import dayjs from "dayjs";
import { validate as validateUUID, v4 as uuidv4 } from "uuid";
import { Logger } from "cms/utils/log/logger";

/**
 * Checks if a variable is defined. Returns true if the variable exists.
 * @param str A variable expected to exist
 * @returns {boolean}
 */
export const exists = function (str) {
    return (str !== undefined && str !== null);
};
/**
 * Checks if a string is empty. Returns true if the string has no length. also works for arrays
 * @param str A variable expected to exist
 * @returns {boolean}
 */
export const isEmpty = function (str) {
    return (str.length === 0);
};
/**
 * Checks if a string is not empty. Returns true if the string has length. Also works for arrays.
 * @param str A variable expected to exist
 * @returns {boolean}
 */
export const isNotEmpty = function (str) {
    return (str.length !== 0);
};
/**
 * Checks if a variable is a string. Returns true if the variable is a string.
 * @param {unknown} str A variable expected to contain a string
 * @returns {str is string}
 */
export const isString = function (str) {
    return (typeof str === "string");
};
export const isNull = function (str) {
    return (str === null);
};
export const isNotNull = function (str) {
    return (str !== null);
};
export const isUndefined = function (str) {
    return (typeof str === "undefined");
};

/**
 * Checks if a string is a numeric value.
 * @param str
 * @returns {boolean}
 */
function isNumericString (str) {
    if (typeof str != "string") {
        return false;
    }
    return !isNaN(str) && !isNaN(parseFloat(str));
}

/**
 * Checks if a variable is a number. Returns true if the variable is a string.
 * @param number A variable expected to contain a number
 * @returns {number is number}
 */
export const isNumber = function (number) {
    return (typeof number === "number");
};
/**
 * Checks if a variable is a boolean. Returns true if the variable is a string.
 * @param str A variable expected to contain a number
 * @returns {boolean}
 */
export const isBoolean = function (str) {
    return (typeof str === "boolean");
};
/**
 * Checks if a variable is a Truthy. Returns true if the variable is a string.
 * @param str A variable expected to contain a number
 * @returns {boolean}
 */
export const isTrue = function (str) {
    return Boolean(str).valueOf();
};
export const isFalse = function (str) {
    return !Boolean(str).valueOf();
};
export const isObject = function (obj) {
    // typeof returns 'object' for null and [...]
    return (typeof obj === "object" && obj !== null && isNotArray(obj));
};
export const objectHasProperty = function (obj, prop) {
    if (isNull(obj)) {
        return false; 
    }
    const wasObject = isObject(obj);
    if (!wasObject) {
        return false; 
    }
    const hasProperty = obj.hasOwnProperty(prop);
    const didExist = exists(obj[prop]);
    return (wasObject && hasProperty && didExist);
};
export const objectHasKeys = function (obj) {
    const itExists = exists(obj);
    if (!itExists) {
        return false; 
    }
    const isObj = isObject(obj);
    const hasKeys = Object.keys(obj).length;
    return (itExists && isObj && hasKeys);
};
export const isObjectEmpty = function (obj) {
    for (const prop in obj) {
        if (Object.hasOwn(obj, prop)) {
            return false;
        }
    }
    return true;
}; 
/**
 * Checks if a object variable exists and contains a string with some length.
 * Returns true if the variable is an object and has a property identified by 'prop'.
 * That property must also not be an empty string.
 * @param obj
 * @param prop
 * @returns {*|boolean}
 */
export const objectHasPropertyString = function (obj, prop) {
    const hasProperty = objectHasProperty(obj, prop);
    if (!hasProperty) {
        return false; 
    }
    const wasString = isStringNotEmpty(obj[prop]);
    return (hasProperty && wasString);
};
export const objectHasPropertyBoolean = function (obj, prop) {
    const hasProperty = objectHasProperty(obj, prop);
    if (!hasProperty) {
        return false; 
    }
    const wasBoolean = isBoolean(obj[prop]);
    return (hasProperty && wasBoolean);
    
};
export const objectHasPropertyNumber = function (obj, prop) {
    const hasProperty = objectHasProperty(obj, prop);
    if (!hasProperty) {
        return false; 
    }
    const wasNumber = isNumber(obj[prop]);
    return (hasProperty && wasNumber);
};
export const isArray = function (obj) {
    const wasArray = Array.isArray(obj);
    return (wasArray);
};
export const isNotArray = function (obj) {
    const wasArray = Array.isArray(obj);
    return (!wasArray);
};
export const objectHasPropertyArray = function (obj, prop) {
    const hasProperty = objectHasProperty(obj, prop);
    if (!hasProperty) {
        return false; 
    }
    const wasArray = isArray(obj[prop]);
    return (hasProperty && wasArray);
};
export const isArrayNotEmpty = function (obj) {
    const doesExist = exists(obj);
    const wasArray = doesExist && isArray(obj);
    return (wasArray && isNotEmpty(obj));
};
export const isArrayEmpty = function (obj) {
    const wasArray = isArray(obj);
    return (wasArray && isEmpty(obj));
};
const isEqualTo = (a, b) => {
    return a === b;
};
const isLessThan = (a, b) => {
    return a < b;
};
const isGreaterThan = (a, b) => {
    return a > b;
};
/**
 * Checks if an array exists and if its length is greater than the provided number.
 * Returns true if the variable both is an array and its length is greater than,
 * or equal to, the number provided.
 * @param obj The array. Must be an Array.
 * @param num The number to test against. Must be a number.
 * @returns {boolean}
 */
export const isArrayGreaterThan = function (obj,num) {
    if(!isNumber(num)) {
        throw Error("Must supply a number for isArrayGreaterThan");
    }
    const a = obj.length;
    const b = Number(num).valueOf();
    return ( isArrayNotEmpty(obj) && isGreaterThan(a,b) );
};
/**
 * Checks if an array exists and if its length is greater than or equal to the
 * provided number. Returns true if the variable both is an array and its
 * length is greater than, or equal to, the number provided.
 * @param obj The array. Must be an Array.
 * @param num The number to test against. Must be a number.
 * @returns {boolean}
 */
export const isArrayEqualOrGreaterThan = function (obj,num) {
    if(!isNumber(num)) {
        throw Error("Must supply a number for isArrayEqualOrGreaterThan");
    }
    const a = obj.length;
    const b = Number(num).valueOf();
    if (isLessThan(a, b)) {
        return false;
    }
    return (
        isArrayNotEmpty(obj) && (isEqualTo(a, b) || isGreaterThan(a, b))
    );
};
/**
 * Checks if an array exists and if its length is less than the provided number.
 * Returns true if the variable both is an array and its length is less than
 * the number provided.
 * @param obj The array. Must be an Array.
 * @param num The number to test against. Must be a number.
 * @returns {boolean}
 */
export const isArrayLessThan = function (obj,num) {
    if(!isNumber(num)) {
        throw Error("Must supply a number for isArrayLessThan");
    }
    const a = obj.length;
    const b = Number(num).valueOf();
    const isLessThan = a < b;
    return ( isArrayNotEmpty(obj) && isLessThan );
};
/**
 * Checks if an array exists and if it only has one member item.
 * Returns true if the variable both is an array and its length is exactly equal
 * to 1.
 * @param obj The array. Must be an Array.
 * @returns {boolean}
 */
export const isArraySingleValue = function (obj) {
    const isOne = obj.length === 1;
    return ( isArrayNotEmpty(obj) && isOne );
};
/**
 * Checks if an array exists and if its length is equal to the provided number.
 * Returns true if the variable both is an array and its length is exactly equal
 * to the number provided.
 * @param obj The array
 * @param num The number to test against. Must be a number.
 * @returns {boolean}
 */
export const isArrayCountEqual = function (obj,num) {
    if(!isNumber(num)) {
        throw Error("Must supply a number for isArrayCountEqual");
    }
    const a = obj.length;
    const b = Number(num).valueOf();
    return ( isArrayNotEmpty(obj) && (a === b) );
};

/**
 * Checks if a variable exists and is a string. Returns true if the variable both exists and is a string
 * @param str
 * @returns {boolean}
 */
export const isStringExists = function (str) {
    return (exists(str) && isString(str));
};

/**
 * Checks if a string is not empty. Returns true is the variable exists, is a string and has length.
 * Returns false for undefined or empty strings.
 * @param str
 * @returns {boolean}
 */
export const isStringNotEmpty = function (str) {
    return (exists(str) && isString(str) && isNotEmpty(str));
};
/**
 * Checks if a string is empty (ie. ""). Returns true is the variable exists, is a string, and has
 *   no length. Returns false for undefined or null strings.
 * @param str
 * @returns {boolean}
 */
export const isStringEmpty = function (str) {
    return (exists(str) && isString(str) && isEmpty(str));
};
/**
 * Checks if a string does not exist or is empty. Returns true is the variable does not exist, 
 *   or if it is not a string, or if it has no length. Returns false if it is an actual string 
 *   that has length.
 * @param str
 * @returns {boolean}
 */
export const isNotString = function (str) {
    if (!exists(str)) {
        return true;
    }
    if (!isString(str)) {
        return true;
    }
    if (isEmpty(str)) {
        return true;
    }
    return false;
};
/**
 * Checks if a string is empty. Returns true is the variable exists, is a string and has length.
 * Returns false for undefined or empty strings.
 * @param number
 * @returns {boolean}
 */
export const isNumberInteger = function (number) {
    return (exists(number) && isNumber(number) && Number.isInteger(number) );
};
/**
 * Checks if a string is a not empty GUID. Returns true is the variable exists, is a string is a guid pattern.
 * Returns false for undefined or empty strings.
 * @param string
 * @returns {boolean}
 */
export const isGuid = function (str) {
    return exists(str) && isStringNotEmpty(str) && isValidGuid(str);
};
/**
 * Checks if a string is a number. Returns true is the variable exists, and is a string and that string
 * will parse to a number. Returns false for undefined variables, variables that are not strings
 * or strings that are not numbers.
 * @param str
 * @returns {boolean}
 */
export const isStringANumber = function (str) {
    return (exists(str) && isNumericString(str));
};
/**
 * Tests if an object is found in an array of options objects, by comparing the 'value'
 * property of each.
 * @param option The option you want to match. Must be an Object with a 'value' property.
 * @param options The array of options. Must be an array, Each object must have a
 * value property.
 * @returns {boolean}
 */
export const isValidOption = (option, options) => {
    if (isArrayNotEmpty(options)) {
        return options.find((element) => option === element.value);
    }
    return false;
};

/**
 * Tests if a string is a valid Date by parsing the date and checking if that can be
 * represented as a number. Uses isNaN.
 * @param dateStr The option you want to match. Must be an Object with a 'value' property.
 * @param shouldThrow provide true (boolean) to cause this function to throw an error when
 * the input is not a string
 * @returns {boolean}
 */
export const isValidDate = (dateStr, shouldThrow) => {
    if (!isStringNotEmpty(dateStr)) {
        if (shouldThrow) {
            throw Error("Must supply a number for isArrayLessThan");
        } else {
            return false;
        }
    }
    return !isNaN(Date.parse(dateStr));
    // TODO: below may be better but is untested
    // const date = dayjs(dateStr,[
    //     Config.dates.display.date,
    //     Config.dates.display.datetime
    // ])
    // return date.isValid()
};
/**
 * Tests if a string is a valid representation of time, using the dayjs framework. It
 * attempts to parse the string into a date using dayjs by providing both time
 * and datetime format strings (an array), and them using dayjs's isValid check.
 * @param timeStr The string you what to test. Must be a String.
 * @returns {boolean}
 */
export const isValidDisplayTime = (timeStr) => {
    const time = dayjs(timeStr,[
        Config.dates.display.time,
        Config.dates.display.datetime
    ]);
    const valid = time.isValid();
    return valid;
};
/**
 * Parses a string using the dayjs framework into a date by providing
 * both time and datetime format strings. It then converts that to a
 * Date object using .toDate()
 * Works with parsing display formats. Does not work with parsing Storage formats
 * @param timeStr The string you what to test. Must be a String.
 * @returns {Date}
 */
export const parseDisplayTime = (timeStr) => {
    return dayjs(timeStr, [
        Config.dates.display.time,
        Config.dates.display.datetime
    ]).toDate();
};
/**
 * Tests if a string is a valid GUID (Globally Unique Identifier), using
 * the uuid module's validateUUID() function (https://github.com/uuidjs/uuid).
 * @param str The string you what to test. Must be a non-empty String.
 * @returns {boolean}
 */
export const isValidGuid = (str) => {
    return isStringNotEmpty && validateUUID(str);
};
/**
 * Generates a new GUID (Globally Unique Identifier), using
 * the uuid module (https://github.com/uuidjs/uuid).
 * @returns {string}
 */
export const generateGuid = () => {
    return uuidv4();
};

/**
 * Checks if Rich Text is not empty. looks to see if the array of paragraphs is 0
 *
 * @param richText
 * @returns {boolean|boolean}
 */
export const isRichTextNotEmpty = (richText) => {
    if (isObject(richText)) {
        const { content } = richText;
        if (!content) {
            return false;
        }
        const number = content?.length;
        if (number === 1) {
            const innerContent = content[0]?.content;
            if (isArrayNotEmpty(innerContent)) {
                return true;
            } else {
                Logger.info("Richtext was empty");
                return false;
            }
        }
        return true;
    }
};

/**
 * Checks if Rich Text is empty. looks to see if the array of paragraphs is 0
 *
 * @param richText
 * @returns {boolean|boolean}
 */
export const isRichTextEmpty = (richText) => {
    const notEmpty = isRichTextNotEmpty(richText);
    return !notEmpty;
};

/**
 * Checks if the input is an array where the first item is a File Object
 *
 * @param acceptedFiles Array
 * @returns {boolean|boolean}
 */
export const hasFileInArray = (acceptedFiles) => {
    if (isArrayNotEmpty(acceptedFiles)) {
        const file = acceptedFiles[0];
        if (exists(file) && isObject(file)) {
            if (isStringNotEmpty(file?.type)) {
                return true;
            }
        }
    }
    return false;
};
/**
 * Checks if the input is a File Object
 *
 * @param file {Object}
 * @returns {boolean|boolean}
 */
export const hasFile = (file) => {
    if (exists(file) && isObject(file)) {
        if (isStringNotEmpty(file?.type)) {
            return true;
        }
    }
    return false;
};
/**
 * Checks if the input file is the specified type
 *
 * @param file {Object}
 * @param type {string}
 * @returns {boolean|boolean}
 */
export const isSameTypeOfFile = (file, type) => {
    if (file?.type === type) {
        return true;
    }
    return false;
};
